// TinyOpenSourceEditor.tsx
import React, { useRef } from 'react';
import type { Editor as TinyMCEEditor } from 'tinymce';
import { Editor as TinyMCEReactEditor } from '@tinymce/tinymce-react';
import { AxiosError } from 'axios';
import { upload, getImagesInfo } from 'api';
import useError from 'components/ErrorProvider/useError';

// --- ядро/иконки/тема (TinyMCE v5) ---
import 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';

// --- локаль (v5) ---
import 'tinymce-i18n/langs5/ru';

// --- плагины (из твоего примера) ---
import 'tinymce/plugins/print';
import 'tinymce/plugins/preview';
import 'tinymce/plugins/paste';
import 'tinymce/plugins/importcss';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/directionality';
import 'tinymce/plugins/code';
import 'tinymce/plugins/visualblocks';
import 'tinymce/plugins/visualchars';
import 'tinymce/plugins/image';
import 'tinymce/plugins/link';
import 'tinymce/plugins/media';
import 'tinymce/plugins/table';
import 'tinymce/plugins/charmap';
import 'tinymce/plugins/hr';
import 'tinymce/plugins/nonbreaking';
import 'tinymce/plugins/toc';
import 'tinymce/plugins/insertdatetime';
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/wordcount';
import 'tinymce/plugins/textpattern';
import 'tinymce/plugins/noneditable';
import 'tinymce/plugins/help';
import 'tinymce/plugins/quickbars';
import 'tinymce/plugins/emoticons';
import 'tinymce/plugins/emoticons/js/emojis';

// --- стили (обязательно) ---
import 'tinymce/skins/content/default/content.min.css';
import 'tinymce/skins/ui/oxide/skin.min.css';

type IProps = {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  readOnly?: boolean;
};

export const TinymceEditor: React.FC<IProps> = ({ value, onChange, placeholder, readOnly }) => {
  const edRef = useRef<TinyMCEEditor | null>(null);

  const { addError } = useError();

  // SSR-safe определение тёмной темы
  const useDarkMode =
    typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;

  const uploadOne = async (file: File) => {
    try {
      const rels: string[] = await upload([file]);
      const infos = await getImagesInfo(rels);
      return infos[0];
    } catch (error) {
      addError((error as AxiosError)?.response?.data.message || 'Не удалось загрузить файл');
      throw error;
    }
  };

  return (
    <TinyMCEReactEditor
      onInit={(_, ed) => (edRef.current = ed)}
      value={value}
      onEditorChange={onChange}
      disabled={!!readOnly}
      init={{
        // --- общие ---
        language: 'ru',
        menubar: false,
        toolbar_sticky: true,
        height: 600,
        branding: false,
        statusbar: false,
        placeholder,

        // --- плагины/панели ---
        plugins:
          'print preview paste importcss searchreplace autolink directionality code ' +
          'visualblocks visualchars image link media table charmap hr ' +
          'nonbreaking toc insertdatetime advlist lists wordcount ' +
          'textpattern noneditable help charmap quickbars emoticons',

        toolbar:
          'undo redo | bold italic underline strikethrough | ' +
          'alignleft aligncenter alignright alignjustify | outdent indent | numlist bullist | ' +
          'forecolor backcolor removeformat | charmap emoticons | ' +
          'preview print | insertfile table image media link | ltr rtl',

        toolbar_mode: 'sliding' as const,
        contextmenu: 'link image table',
        quickbars_selection_toolbar: 'bold italic | quicklink h1 h2 h3 h4 h5 h6 blockquote | emoticons',

        importcss_append: true,

        // --- таблицы ---
        table_toolbar:
          'tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | ' +
          'tableinsertcolbefore tableinsertcolafter tabledeletecol | tablemergecells tablesplitcells',
        table_default_attributes: { border: '1' },
        table_default_styles: { width: '100%', borderCollapse: 'collapse' },
        object_resizing: 'table',

        // --- настройки диалога ссылок (убираем Title, Target и Anchor) ---
        link_title: false, // скрывает поле "Заголовок"
        target_list: false, // скрывает выбор "Открыть ссылку в..."
        link_default_protocol: 'https',

        // --- медиа (убираем вкладку "Расширенные" у видео) ---
        media_alt_source: false,
        media_poster: false,

        // --- контент внутри iframe ---
        content_style:
          'body { font-family:Roboto,sans-serif; font-size:14px } ' +
          'table { border-collapse:collapse;width:100%} table,td,th { border:1px solid #ccc } td,th { padding:8px }',

        // --- тёмная тема (как в примере tiny) ---
        skin: useDarkMode ? 'oxide-dark' : 'oxide',
        content_css: useDarkMode ? 'dark' : 'default',

        // --- медиа, изображения ---
        image_caption: false,
        automatic_uploads: false,
        paste_data_images: true,
        images_file_types: 'jpeg,jpg,png,webp,gif,svg',
        images_upload_handler: async (blobInfo) => {
          const file = blobInfo.blob() as File;
          const info = await uploadOne(file);
          return info.downloadUrl;
        },

        file_picker_types: 'file image media',
        file_picker_callback: async (cb, _value, meta) => {
          const input = document.createElement('input');
          input.type = 'file';
          input.multiple = meta.filetype === 'file';
          input.accept = meta.filetype === 'image' ? 'image/*' : meta.filetype === 'media' ? 'video/*,audio/*' : '*/*';

          input.onchange = async () => {
            if (!input.files?.length) return;
            const files = Array.from(input.files);
            const info = await uploadOne(files[0]);
            if (meta.filetype === 'image') cb(info.downloadUrl, { alt: files[0].name });
            else if (meta.filetype === 'media') cb(info.downloadUrl, { poster: info.downloadThumbnailUrl });
            else cb(info.downloadUrl, { text: files[0].name }); // просто ссылка на файл
          };

          input.click();
        },

        // --- тех. колбэки/диагностика по желанию ---
        setup: (ed) => {
          edRef.current = ed;
        },
      }}
    />
  );
};
